import Pieces from "./Pieces";
import styles from "./Board.module.css";
import { read } from "../chessUtils/fen";
import React, { useId, useRef } from "react";
import { ChessSets } from "@chesshotel/constants";
import type { Square } from "@chesshotel/chess";
import { getPosFromKey } from "../chessUtils/util";
import NumbersLetters from "./NumbersLetters";

export type DisplayBoardArrow = {
  from: Square;
  to: Square;
  color?: string;
  opacity?: number;
};

export type DisplayBoardHighlightStyle = "fill" | "ring" | "dot";

export type DisplayBoardHighlight = {
  square: Square;
  color?: string;
  opacity?: number;
  style?: DisplayBoardHighlightStyle;
};

type DisplayBoardProps = {
  fen?: string;
  boardSize?: number;
  playingWhite?: boolean;
  chessSet?: ChessSets;
  arrows?: DisplayBoardArrow[];
  highlights?: DisplayBoardHighlight[];
  showCoordinates?: boolean;
  ariaLabel?: string;
};

function getSquareTransform(
  square: Square,
  playingWhite: boolean,
  squareSize: number,
) {
  const pos = getPosFromKey(square, playingWhite);
  return `translate(${pos[0] * squareSize}px, ${pos[1] * squareSize}px)`;
}

function getSquareCenter(square: Square, playingWhite: boolean) {
  const pos = getPosFromKey(square, playingWhite);
  return {
    x: pos[0] * 12.5 + 12.5 / 2,
    y: pos[1] * 12.5 + 12.5 / 2,
  };
}

function sanitizeMarkerPrefix(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, "");
}

export default function DisplayBoard({
  fen,
  boardSize = 504,
  playingWhite = true,
  chessSet = ChessSets.CHESSHOTEL_NEW,
  arrows = [],
  highlights = [],
  showCoordinates = false,
  ariaLabel = "Chess position",
}: DisplayBoardProps) {
  const position = fen ? read(fen) : {};
  const pieceRefs = useRef({});
  const markerPrefix = sanitizeMarkerPrefix(useId());
  const squareSize = boardSize / 8;

  const boardClass = styles[chessSet];

  return (
    <div
      className={`relative bg-cover select-none aspect-square max-w-[100vw] md:w-[504px] md:h-[504px] ${boardClass}`}
      role="img"
      aria-label={ariaLabel}
      style={{
        width: boardSize + "px",
        height: boardSize + "px",
      }}
    >
      {showCoordinates && (
        <NumbersLetters playingWhite={playingWhite} placement="inside" />
      )}
      <Pieces
        freeMove={false}
        playingWhite={playingWhite}
        position={position}
        pieceRefs={pieceRefs}
        chessSet={chessSet}
        boardSize={boardSize}
      />
      {highlights.length > 0 && (
        <div className="absolute inset-0 z-30 pointer-events-none">
          {highlights.map((highlight, index) => {
            const style = highlight.style || "fill";
            const color = highlight.color || "#6f95ba";
            const opacity = highlight.opacity ?? 0.45;

            if (style === "dot") {
              return (
                <div
                  key={`highlight-dot-${highlight.square}-${index}`}
                  style={{
                    transform: getSquareTransform(
                      highlight.square,
                      playingWhite,
                      squareSize,
                    ),
                    width: squareSize,
                    height: squareSize,
                  }}
                  className="absolute top-0 left-0 flex items-center justify-center"
                >
                  <div
                    style={{
                      width: `${Math.max(squareSize * 0.26, 10)}px`,
                      height: `${Math.max(squareSize * 0.26, 10)}px`,
                      borderRadius: "9999px",
                      backgroundColor: color,
                      opacity,
                    }}
                  />
                </div>
              );
            }

            if (style === "ring") {
              return (
                <div
                  key={`highlight-ring-${highlight.square}-${index}`}
                  style={{
                    transform: getSquareTransform(
                      highlight.square,
                      playingWhite,
                      squareSize,
                    ),
                    width: squareSize,
                    height: squareSize,
                    border: `${Math.max(squareSize * 0.08, 3)}px solid ${color}`,
                    opacity,
                    boxSizing: "border-box",
                  }}
                  className="absolute top-0 left-0"
                />
              );
            }

            return (
              <div
                key={`highlight-fill-${highlight.square}-${index}`}
                style={{
                  transform: getSquareTransform(
                    highlight.square,
                    playingWhite,
                    squareSize,
                  ),
                  width: squareSize,
                  height: squareSize,
                  backgroundColor: color,
                  opacity,
                }}
                className="absolute top-0 left-0"
              />
            );
          })}
        </div>
      )}
      {arrows.length > 0 && (
        <div className="absolute inset-0 z-40 pointer-events-none">
          <svg width="100%" height="100%" viewBox="0 0 100 100">
            <defs>
              {arrows.map((arrow, index) => {
                const markerId = `${markerPrefix}-arrow-${index}`;
                const color = arrow.color || "#333333";
                return (
                  <marker
                    key={`marker-${markerId}`}
                    id={markerId}
                    orient="auto"
                    markerWidth="2.5"
                    markerHeight="3"
                    refX="1.5"
                    refY="1.5"
                  >
                    <path d="M0,0 V3 L2.2,1.5 Z" fill={color}></path>
                  </marker>
                );
              })}
            </defs>
            {arrows.map((arrow, index) => {
              const color = arrow.color || "#333333";
              const opacity = arrow.opacity ?? 0.65;
              const from = getSquareCenter(arrow.from, playingWhite);
              const to = getSquareCenter(arrow.to, playingWhite);
              const markerId = `${markerPrefix}-arrow-${index}`;

              const dx = to.x - from.x;
              const dy = to.y - from.y;
              const angle = Math.atan2(dy, dx);
              const xr = Math.cos(angle);
              const yr = Math.sin(angle);

              const shortenStart = 4.5;
              const shortenEnd = 1;

              const x1 = from.x + xr * shortenStart;
              const y1 = from.y + yr * shortenStart;
              const x2 = to.x - xr * shortenEnd;
              const y2 = to.y - yr * shortenEnd;

              return (
                <line
                  key={`arrow-${arrow.from}-${arrow.to}-${index}`}
                  stroke={color}
                  strokeWidth="2%"
                  markerEnd={`url(#${markerId})`}
                  opacity={opacity}
                  x1={`${x1}%`}
                  y1={`${y1}%`}
                  x2={`${x2}%`}
                  y2={`${y2}%`}
                ></line>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}
